import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Loader2, PackageSearch, CircleCheck, Truck, Home, Clock, XCircle } from "lucide-react";
import StoreShell from "@/components/landing/StoreShell";
import { api } from "@/lib/api";
import { useSeo } from "@/hooks/useSeo";

const STEPS = [
    { key: "pending", label: "Order placed", icon: Clock },
    { key: "confirmed", label: "Confirmed", icon: CircleCheck },
    { key: "shipped", label: "Shipped", icon: Truck },
    { key: "delivered", label: "Delivered", icon: Home },
];

const PAY = { cod: "Cash on Delivery", easypaisa: "Easypaisa", jazzcash: "JazzCash", hbl: "HBL Bank Transfer" };

export default function TrackOrderPage() {
    const [params, setParams] = useSearchParams();
    const [number, setNumber] = useState(params.get("order") || "");
    const [order, setOrder] = useState(null);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    useSeo({
        title: "Track Your Order | Resham Drops",
        description: "Track your Resham Drops order status with your order number. Delivery across Pakistan.",
        path: "/track-order",
    });

    const lookup = async (value) => {
        const q = value.trim().toUpperCase();
        if (!q) return;
        setLoading(true);
        setError("");
        setOrder(null);
        try {
            const { data } = await api.get(`/orders/track/${encodeURIComponent(q)}`);
            setOrder(data);
            setParams({ order: q }, { replace: true });
        } catch (e) {
            setError(e?.response?.data?.detail || "Could not find that order.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (params.get("order")) lookup(params.get("order"));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <StoreShell testid="track-order-page">
            <main className="max-w-2xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
                <div className="w-12 h-12 rounded-2xl bg-forest-800 text-gold-400 flex items-center justify-center">
                    <PackageSearch className="w-6 h-6" />
                </div>
                <h1 className="font-serif font-bold text-forest-800 text-4xl sm:text-5xl mt-5">Track your order</h1>
                <p className="text-forest-900/70 text-sm sm:text-base mt-3">
                    Enter the order number from your confirmation (e.g. RD-12345).
                </p>
                <form
                    onSubmit={(e) => {
                        e.preventDefault();
                        lookup(number);
                    }}
                    className="mt-8 flex gap-3"
                >
                    <input
                        data-testid="track-order-input"
                        value={number}
                        onChange={(e) => setNumber(e.target.value)}
                        placeholder="RD-12345"
                        className="flex-1 bg-white border border-forest-100 rounded-xl px-4 py-3 text-sm font-mono uppercase focus:outline-none focus:ring-2 focus:ring-gold-500"
                    />
                    <button
                        type="submit"
                        data-testid="track-order-submit"
                        disabled={loading}
                        className="inline-flex items-center gap-2 bg-forest-800 hover:bg-forest-900 disabled:opacity-70 text-gold-400 font-bold text-sm px-6 py-3 rounded-xl border border-gold-500/40"
                    >
                        {loading && <Loader2 className="w-4 h-4 animate-spin" />} Track
                    </button>
                </form>
                {error && (
                    <p data-testid="track-order-error" className="mt-4 text-sm text-rose-700 font-semibold">
                        {error}
                    </p>
                )}
                {order && <TrackResult order={order} />}
            </main>
        </StoreShell>
    );
}

function TrackResult({ order }) {
    const cancelled = order.status === "cancelled";
    const current = STEPS.findIndex((s) => s.key === order.status);
    const at = (key) => order.status_history?.findLast?.((h) => h.status === key)?.at;
    return (
        <div data-testid="track-order-result" className="mt-8 bg-white rounded-2xl border border-forest-100 shadow-sm p-6 sm:p-8">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
                <p className="font-serif font-bold text-forest-800 text-xl">#{order.order_number}</p>
                <span data-testid="track-order-status" className="text-xs font-bold uppercase tracking-widest bg-forest-50 text-forest-800 px-3 py-1 rounded-full">
                    {order.status}
                </span>
            </div>
            <p className="text-sm text-forest-900/70 mt-1">
                {order.customer_first_name && `Hi ${order.customer_first_name} · `}
                {order.product_name} · {order.bundle_label}
            </p>
            {cancelled ? (
                <div className="mt-6 flex items-center gap-2 text-rose-700 font-semibold text-sm">
                    <XCircle className="w-5 h-5" /> This order was cancelled. Email us at reshamdrops@gmail.com if this is a mistake.
                </div>
            ) : (
                <ol className="mt-8 space-y-5">
                    {STEPS.map((s, i) => {
                        const done = i <= current;
                        const time = i === 0 ? order.created_at : at(s.key);
                        return (
                            <li key={s.key} data-testid={`track-step-${s.key}`} className="flex items-center gap-4">
                                <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${done ? "bg-forest-800 text-gold-400" : "bg-forest-50 text-forest-300"}`}>
                                    <s.icon className="w-5 h-5" />
                                </div>
                                <div>
                                    <p className={`font-bold text-sm ${done ? "text-forest-900" : "text-forest-900/40"}`}>{s.label}</p>
                                    {done && time && <p className="text-xs text-forest-700/70">{new Date(time).toLocaleString()}</p>}
                                </div>
                            </li>
                        );
                    })}
                </ol>
            )}
            <div className="mt-8 border-t border-forest-100 pt-4 grid grid-cols-2 gap-3 text-sm">
                <div>
                    <p className="text-[11px] uppercase tracking-widest text-forest-700/70 font-bold">Total</p>
                    <p className="font-bold text-forest-900">{Number(order.total_price || 0).toLocaleString()} PKR</p>
                </div>
                <div>
                    <p className="text-[11px] uppercase tracking-widest text-forest-700/70 font-bold">Payment</p>
                    <p className="font-bold text-forest-900">{PAY[order.payment_method] || order.payment_method}</p>
                </div>
                <div>
                    <p className="text-[11px] uppercase tracking-widest text-forest-700/70 font-bold">Delivering to</p>
                    <p className="font-bold text-forest-900">{order.city}</p>
                </div>
            </div>
        </div>
    );
}
